//* Vue
import type { SetupContext } from "vue";
//* 组件库
import type { UiInput, UiSelect, UiTypes } from "various-ui";
//* 组件属性
import type { SelectSearchProps, SelectSearchEmits } from "../index";
//* 样式
import css from "../style/index.module.less";

export const useComposable = (define: SelectSearchProps, emit: SetupContext<typeof SelectSearchEmits>["emit"]) => {
    //* 插件
    const i18n = useI18n();
    const route = useRoute();

    //* 静态属性
    const vars: { mode: UiTypes.candidate[]; datas: any[]; timeout?: NodeJS.Timeout; controller?: AbortController } = {
        datas: [],
        mode: [
            { label: i18n.t("All"), value: "" },
            { label: i18n.t("Disease"), value: "disease" },
            { label: i18n.t("Gene"), value: "gene" },
            { label: i18n.t("Model"), value: "model" },
            { label: i18n.t("Mutation"), value: "mutation" },
            { label: i18n.t("Tools", 2), value: "tool" },
        ],
    };

    //* 响应式变量
    const refs = {
        mode: ref<string>(""), //* 搜索类型
        size: ref<number>(416),
        keyword: ref<string>(define.modelValue),
        visible: ref<boolean>(false),
        candidates: ref<UiTypes.candidate[]>([]),
    };

    //* 节点
    const nodes = {
        input: ref<InstanceType<typeof UiInput>>(),
        select: ref<InstanceType<typeof UiSelect>>(),
        options: ref<HTMLDivElement>(),
        container: ref<HTMLDivElement>(),
    };

    //* 事件列表
    const methods = {
        //* 初始化函数
        init: () => {
            //* 初始化搜索框类型
            //* 导航搜索固定为全部，其他的根据路由判断
            const routeName = route.meta?.route;
            if (define.navigation) refs.mode.value = "";
            else if (routeName == "SearchGene") refs.mode.value = "gene";
            else if (routeName == "SearchModel") refs.mode.value = "model";
            else if (routeName == "SearchDisease") refs.mode.value = "disease";
            else if (routeName == "SearchMutation") refs.mode.value = "mutation";
            else if (routeName == "SearchTool") refs.mode.value = "tool";
            else {
                refs.mode.value = "";
            }
        },

        //* 候选项更多函数
        more: () => {
            vars.timeout && clearTimeout(vars.timeout);
            vars.timeout = setTimeout(() => {
                if (!nodes.options.value) return;
                if (nodes.options.value.scrollTop + nodes.options.value.clientHeight >= nodes.options.value.scrollHeight * 0.8) {
                    const index = Math.ceil(refs.candidates.value.length / 25);
                    if (index < Math.ceil(vars.datas.length / 25)) {
                        refs.candidates.value.push(...methods.dispose(vars.datas.slice(index * 25, (index + 1) * 25)));
                    }
                }
            }, 100);
        },

        //* 窗口大小改变函数
        resize: () => {
            if (nodes.container.value) {
                //* 数据初始化
                const rect = nodes.container.value.getBoundingClientRect();
                const size = rect.top + rect.height;
                //* 检查 div 是否超出可视区域底部
                if (!(size + 416 > window.innerHeight)) refs.size.value = 416;
                else {
                    refs.size.value = 416 - Math.ceil((size - window.innerHeight + 416) / 40) * 40;
                    if (refs.size.value < 96) refs.size.value = 96;
                    else if (refs.size.value > 416) {
                        refs.size.value = 416;
                    }
                }
            }
        },

        //* 候选项选择函数
        select: (option: UiTypes.candidate) => {
            //* 检测是否需要切换搜索类型（导航搜索固定不切换）
            const mode = vars.mode.find((value) => value.value == option.type);
            if (mode && !define.navigation) refs.mode.value = mode.value;
            else {
                refs.mode.value = "";
            }

            //* 选中选项赋值
            refs.keyword.value = option.value;

            //* 事件响应
            emit("update:modelValue", refs.keyword.value);
            emit("search", refs.keyword.value, refs.mode.value || mode?.value, option.id);
        },

        //* 候选项查询函数
        suggest: (type?: string) => {
            refs.visible.value = true;
            if (!(type == "focus" && refs.candidates.value.length)) {
                //* 数据初始化
                const keyword = refs.keyword.value.trim();
                //* 创建接口控制器
                vars.controller = new AbortController();
                //* 接口请求
                api.frontend("/api/search/suggest", {
                    method: "POST",
                    data: { mode: refs.mode.value || "all", keyword: keyword },
                    signal: vars.controller.signal,
                    success: (response: { data: any[]; datas: any[] }) => {
                        //* 数据处理
                        vars.datas = response.datas || [];
                        refs.candidates.value = [...methods.dispose(response.data)];
                        //* 回到顶部
                        nextTick(() => {
                            nodes.options.value?.scrollTo({ top: 0 });
                        });
                    },
                });
            }
        },

        //* 数据处理
        dispose: (data: any[]) => {
            return (
                data?.map((value) => {
                    const name = value.text || "";
                    return {
                        id: value.rel_id || "",
                        type: value.type || "",
                        value: name,
                        label: useModelNameAnalyze(name),
                        species: value.rel_tax_id,
                        alias: value.alias,
                    };
                }) || []
            );
        },

        //* 替换字符串
        replace_name: (name: string) => {
            // 使用正则表达式，i标志表示不区分大小写
            const regex = new RegExp(refs.keyword.value, "i");
            return name.replace(regex, (match) => {
                return `<span class="font-bold">${match}</span>`;
            });
        },
    };

    //* 动态属性
    const binds = reactive({
        options: computed(() => {
            return { style: { "max-height": refs.size.value + "px" } };
        }),

        input: computed(() => {
            //* 数据初始化
            const result = {
                widthExtra: nodes.input.value?.$el.clientWidth,
                width: "100%",
                height: refs.size.value,
                maxlength: 1024,
                animation: false,
                placeholder: define.placeholder,
                classExtraName: css.candidates,
            };

            if (!result.placeholder) {
                switch (refs.mode.value) {
                    case "":
                        result.placeholder = i18n.t("Placeholder.Search");
                        break;
                    case "disease":
                        result.placeholder = i18n.t("Placeholder.SearchDisease");
                        break;
                    case "gene":
                        result.placeholder = i18n.t("Placeholder.SearchGene");
                        break;
                    case "model":
                        result.placeholder = i18n.t("Placeholder.SearchModel");
                        break;
                    case "mutation":
                        result.placeholder = i18n.t("Placeholder.SearchMutation");
                        break;
                    case "tool":
                        result.placeholder = i18n.t("Placeholder.SearchTool");
                        break;
                }
            }

            return result;
        }),
    });

    //* 响应事件
    const ons = {
        input: {
            blur: () => (vars.controller?.abort(), requestAnimationFrame(() => (refs.visible.value = false))),
            focus: () => methods.suggest("focus"),
            enter: (ev: Event) => (emit("search", refs.keyword.value, refs.mode.value), nodes.input.value?.blur(ev)),
            input: () => {
                //* 取消接口请求
                vars.controller?.abort();
                //* 事件响应
                emit("update:modelValue", refs.keyword.value.trim());
                //* 触发候选项查询
                methods.suggest();
            },
        },
    };

    return { ons, vars, refs, nodes, binds, methods };
};
